import { IllegalOperationException } from '../../exceptions/illegal-operation-exception.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { Result, VoidResult } from '../../utility/result.js';
import { ErrorLike } from './error-like.js';

export class VoidOperationResponse {
  public readonly success: boolean;
  private readonly _error: ErrorLike | null;

  protected constructor(success: boolean, error: ErrorLike | null) {
    this.success = success;
    this._error = error;
  }

  get error(): ErrorLike {
    if(isNullOrUndefined(this._error)) {
      throw new IllegalOperationException('Error is inaccessible on a success instance');
    }

    return this._error;
  }

  toCleanHttpResponse(): Record<string, unknown> {
    return this.success ? { success: true } : { success: false, error: this._error?.toString() };
  }

  toCleanResult(): VoidResult<string> {
    return this.success ? VoidResult.ok() : VoidResult.error(this.error.toString());
  }

  static success(): VoidOperationResponse {
    return new VoidOperationResponse(true, null);
  }

  static error(error: ErrorLike) {
    return new VoidOperationResponse(false, error);
  }

  static toHttpSuccessResponse(): Record<string, unknown> {
    return VoidOperationResponse.success().toCleanHttpResponse();
  }

  static toHttpErrorResponse(error: ErrorLike): Record<string, unknown> {
    return VoidOperationResponse.error(error).toCleanHttpResponse();
  }
}

export class OperationResponse<T> extends VoidOperationResponse {
  public readonly dataOrNull: T | null;

  private constructor(success: boolean, data: T | null, error: ErrorLike | null) {
    super(success, error);
    this.dataOrNull = data;
  }

  get data(): T {
    if(isNullOrUndefined(this.dataOrNull)) {
      throw new IllegalOperationException('Data value is inaccessible on an error instance');
    }

    return this.dataOrNull;
  }

  get hasData(): boolean {
    return isNotNullOrUndefined(this.dataOrNull);
  }

  toDataHttpResponse(converter?: (data: T) => unknown): Record<string, unknown> {
    return this.success ?
        (this.hasData ? { success: true, result: (converter ? converter(this.data) : this.data) } : { success: true }):
        { success: false, error: this.error };
  }

  toDataResult(): Result<T, string> {
    return this.success ? Result.ok(this.data) : Result.error(this.error.toString());
  }

  static success<T>(data: T | null = null): OperationResponse<T> {
    return new OperationResponse<T>(true, data, null);
  }

  static error<T>(error: ErrorLike): OperationResponse<T> {
    return new OperationResponse<T>(false, null, error);
  }

  static toHttpSuccessResponse<T>(data: T | null = null): Record<string, unknown> {
    return OperationResponse.success<T>(data).toDataHttpResponse();
  }

  static toHttpErrorResponse<T>(error: ErrorLike): Record<string, unknown> {
    return OperationResponse.error<T>(error).toDataHttpResponse();
  }
}
